import { useEffect, useState } from 'react'
import {
  Accordion,
  Button,
  ButtonGroup,
  Card,
  FormField,
  InlineAlert,
  Modal,
  Stepper,
} from '@design-systems/canvas-ui'
import Icon, { PersonIcon, TrashIcon } from '@design-systems/react-icons'
import { getApiErrorMessage } from '../../../shared/api/apiClient'
import {
  BRAND_REASON_OPTIONS,
  BRAND_TYPE_OPTIONS,
  BRAND_TYPES,
  CREATE_BRAND_STEPS,
  TARGET_MASTER_BRAND,
} from '../config/brandSetupConfig'
import { createBrandSlug } from '../mappers/brandRequestMapper'
import { useBrand } from '../../../shared/brand/context/BrandContext'
import {
  mapBrandSearchResult,
  mapUserSearchResult,
} from '../mappers/brandLookupMapper'
import { brandSetupService } from '../services/brandSetupService'
import BrandLookupField from './BrandLookupField'
import UserIdentitySummary from './UserIdentitySummary'
import UserLookupField from './UserLookupField'
import './BrandSetupModal.css'

const isValidDirectoryUser = (user) =>
  Boolean(user?.login_id && user?.email && user?.name)

const isValidBrandSelection = (brand) =>
  Boolean(brand?.brand_id && brand?.brand_name)

const SubmissionErrorAlert = ({ isEditMode, message }) =>
  message ? (
    <div className="create-brand-modal__submit-alert">
      <InlineAlert
        heading={`Unable to ${isEditMode ? 'update' : 'create'} brand`}
        variant="error"
      >
        {message}
      </InlineAlert>
    </div>
  ) : null

const MASTER_BRAND_REASON = BRAND_REASON_OPTIONS.find(
  ({ id }) => id === 'first-run',
)

const normalizeSteward = (steward) => {
  const user = mapUserSearchResult(steward || {})
  return user.name || user.email || user.login_id ? user : null
}

const normalizeSelectedBrand = (brand = {}) => {
  const selectedBrand = mapBrandSearchResult(
    brand.selectedBrand || {
      brand_description: brand.brand_description || brand.brandDescription,
      brand_id: brand.brand_id || brand.brandId || brand.externalBrandId,
      brand_name: brand.brand_name || brand.name || brand.brandName,
      is_target_owned: brand.is_target_owned ?? brand.isTargetOwned,
    },
  )
  return isValidBrandSelection(selectedBrand) ? selectedBrand : null
}

const normalizeOption = (option) =>
  option
    ? {
        ...option,
        id: option.id || option.value,
        value: option.value || option.id,
      }
    : null

const normalizeText = (value) => (value || '').trim()

const normalizeStatus = (value) => normalizeText(value).toUpperCase()

const isDraftBrandLifecycle = (brand = {}) =>
  normalizeStatus(brand.lifecycleStatus || brand.brand_lifecycle_status) ===
  'DRAFT'

const createCategorySignature = (category) => {
  if (!category) return ''

  const code = normalizeText(category.code).toLowerCase()
  if (code) return `code:${code}`

  return `id:${normalizeText(category.id)}`
}

const createStewardSignature = (steward) => {
  const normalizedSteward = normalizeSteward(steward)

  if (!normalizedSteward) return ''

  return [
    normalizeText(normalizedSteward.login_id).toLowerCase(),
    normalizeText(normalizedSteward.email).toLowerCase(),
    normalizeText(normalizedSteward.name),
  ].join('|')
}

const createBrandSelectionSignature = (brand) => {
  const normalizedBrand = normalizeSelectedBrand(brand)

  if (!normalizedBrand) return ''

  return [
    normalizeText(normalizedBrand.brand_id),
    normalizeText(normalizedBrand.brand_name).toLowerCase(),
  ].join('|')
}

const areStewardListsEqual = (currentStewards = [], initialStewards = []) => {
  if (currentStewards.length !== initialStewards.length) return false

  return currentStewards.every(
    (steward, index) =>
      createStewardSignature(steward) ===
      createStewardSignature(initialStewards[index]),
  )
}

const hasEditChanges = (form, initialForm) => {
  if (normalizeText(form.brandName) !== normalizeText(initialForm.brandName)) {
    return true
  }

  if (
    createBrandSelectionSignature(form.selectedBrand) !==
    createBrandSelectionSignature(initialForm.selectedBrand)
  ) {
    return true
  }

  if (normalizeText(form.slug) !== normalizeText(initialForm.slug)) {
    return true
  }

  if (
    createCategorySignature(form.category) !==
    createCategorySignature(initialForm.category)
  ) {
    return true
  }

  if (
    createStewardSignature(form.primarySteward) !==
    createStewardSignature(initialForm.primarySteward)
  ) {
    return true
  }

  return !areStewardListsEqual(
    form.supportingStewards,
    initialForm.supportingStewards,
  )
}

const resolveReasonOption = (reason) =>
  typeof reason === 'object'
    ? normalizeOption(reason)
    : BRAND_REASON_OPTIONS.find(({ value }) => value === reason) || null

const createInitialForm = (initialBrandType, initialBrand = {}) => {
  const brand = initialBrand || {}
  const brandType = brand.brandType || initialBrandType
  const isMasterBrand = brandType === BRAND_TYPES.MASTER
  const normalizedCategory = normalizeOption(brand.category)
  const primarySteward = normalizeSteward(
    brand.primarySteward || {
      email: brand.primaryStewardEmail,
      name: brand.primaryStewardName,
    },
  )

  return {
    brandName:
      brand.name ||
      brand.brandName ||
      (isMasterBrand ? TARGET_MASTER_BRAND.name : ''),
    brandType,
    category: normalizedCategory,
    primarySteward,
    reason:
      resolveReasonOption(brand.reason) ||
      (isMasterBrand ? MASTER_BRAND_REASON : null),
    slug:
      brand.slug ||
      brand.brandSlug ||
      (isMasterBrand ? TARGET_MASTER_BRAND.slug : ''),
    selectedBrand: normalizeSelectedBrand(brand),
    supportingStewards: (brand.supportingStewards || []).map(normalizeSteward),
  }
}

const isDetailsValid = (form) =>
  Boolean(
    form.brandType &&
    form.brandName.trim() &&
    form.slug.trim() &&
    form.category?.id &&
    form.reason &&
    isValidBrandSelection(form.selectedBrand),
  )

const isOwnershipValid = (form) =>
  Boolean(
    isValidDirectoryUser(form.primarySteward) &&
    form.supportingStewards.every(isValidDirectoryUser),
  )

const ReviewSection = ({ rows, title }) => (
  <Card as="section" className="create-brand-review__section" variant="border">
    <h3>{title}</h3>
    <dl>
      {rows.map(({ label, value }) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value || '—'}</dd>
        </div>
      ))}
    </dl>
  </Card>
)

// Scrolls on its own once there are more stewards than fit.
const OwnershipReview = ({ primarySteward, supportingStewards = [] }) => (
  <section aria-label="Ownership" className="create-brand-review__stewards">
    <Card
      as="article"
      className="create-brand-review__steward"
      variant="border"
    >
      <p>Primary steward</p>
      <UserIdentitySummary showRole={false} user={primarySteward} />
    </Card>
    {supportingStewards.map((steward, index) => (
      <Card
        as="article"
        className="create-brand-review__steward"
        key={`${steward.login_id}-${index}`}
        variant="border"
      >
        <p>Supporting steward {index + 1}</p>
        <UserIdentitySummary showRole={false} user={steward} />
      </Card>
    ))}
  </section>
)

const BrandTypeSelector = ({ onChange, options, value }) => (
  <fieldset className="brand-type-selector">
    <legend>
      Brand type <span aria-hidden="true">*</span>
    </legend>
    <ButtonGroup>
      {options.map((option) => (
        <Button
          aria-pressed={option.value === value}
          className="brand-type-selector__option"
          key={option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </Button>
      ))}
    </ButtonGroup>
  </fieldset>
)

// Done steps can be revisited; later ones open only through Next.
const CreationStepper = ({ currentStep, onSelect }) => (
  <Stepper
    activeStep={CREATE_BRAND_STEPS[currentStep]}
    aria-label="Brand creation progress"
    className="create-brand-modal__stepper"
    onStepSelect={(_event, { name }) =>
      onSelect(CREATE_BRAND_STEPS.indexOf(name))
    }
  >
    {CREATE_BRAND_STEPS.map((step, index) => (
      <Stepper.Item
        isCompleted={index < currentStep}
        isDisabled={index > currentStep}
        key={step}
        name={step}
      />
    ))}
  </Stepper>
)

const SupportingStewardsFields = ({
  emptyText = 'None added yet.',
  form,
  onAdd,
  onRemove,
  onUpdate,
  searchUsers,
}) => (
  <div className="supporting-stewards">
    <div className="supporting-stewards__heading">
      <span>Supporting stewards</span>
      <Button onClick={onAdd} size="sm" variant="ghost">
        + Add steward
      </Button>
    </div>
    {form.supportingStewards.length === 0 && (
      <p className="supporting-stewards__empty">{emptyText}</p>
    )}
    {form.supportingStewards.map((steward, index) => (
      <div className="supporting-stewards__row" key={`steward-${index + 1}`}>
        <UserLookupField
          id={`supporting-steward-${index}`}
          label={`Supporting steward ${index + 1}`}
          onChange={(user) => onUpdate(index, user)}
          searchUsers={searchUsers}
          value={steward}
        />
        <Button
          aria-label={`Remove supporting steward ${index + 1}`}
          isIconOnly
          onClick={() => onRemove(index)}
          size="sm"
          variant="ghost-neutral"
        >
          <Icon size="sm" svg={TrashIcon} />
        </Button>
      </div>
    ))}
  </div>
)

const BrandSetupModal = ({
  allowedBrandTypes = [BRAND_TYPES.OWNED, BRAND_TYPES.MASTER],
  initialBrand = null,
  initialBrandType = BRAND_TYPES.OWNED,
  isOpen,
  isSubmitting = false,
  mode = 'create',
  onClose,
  onSubmit,
  searchBrands: searchBrandsProp,
  searchUsers: searchUsersProp,
}) => {
  const isEditMode = mode === 'edit'
  const brandTypeOptions = isEditMode
    ? BRAND_TYPE_OPTIONS
    : BRAND_TYPE_OPTIONS.filter(({ value }) =>
        allowedBrandTypes.includes(value),
      )
  const [currentStep, setCurrentStep] = useState(0)
  const [form, setForm] = useState(() =>
    createInitialForm(initialBrandType, initialBrand),
  )
  const [submitError, setSubmitError] = useState('')
  const {
    activeCategories,
    error: categoryRequestError,
    isLoading: isLoadingCategories,
    masterBrandCategory,
  } = useBrand()
  const searchBrands = searchBrandsProp || brandSetupService.searchBrands
  const searchUsers = searchUsersProp || brandSetupService.searchUsers
  const isMasterBrand = form.brandType === BRAND_TYPES.MASTER
  const canEditBrandIdentity =
    !isEditMode || isDraftBrandLifecycle(initialBrand)
  const categoryError = categoryRequestError
    ? 'We could not load brand categories. Please try again.'
    : isMasterBrand && !isLoadingCategories && !masterBrandCategory
      ? 'Please create a master_brand category before creating a master brand.'
      : ''

  useEffect(() => {
    if (!isOpen || activeCategories.length === 0) return

    setForm((current) => {
      const category =
        current.brandType === BRAND_TYPES.MASTER
          ? masterBrandCategory
          : activeCategories.find(
              ({ code, id }) =>
                id === current.category?.id ||
                (code &&
                  code.toLowerCase() === current.category?.code?.toLowerCase()),
            )

      return category && category.id !== current.category?.id
        ? { ...current, category }
        : current
    })
  }, [activeCategories, isOpen, masterBrandCategory])

  if (!isOpen) return null

  const updateForm = (changes) =>
    setForm((current) => ({ ...current, ...changes }))

  const handleNameChange = ({ target: { value } }) => {
    setForm((current) => ({
      ...current,
      brandName: value,
      selectedBrand: null,
      slug: createBrandSlug(value),
    }))
  }

  const handleBrandTypeChange = (brandType) =>
    setForm((current) => {
      const isMasterBrand = brandType === BRAND_TYPES.MASTER
      const wasMasterBrand = current.brandType === BRAND_TYPES.MASTER

      return {
        ...current,
        brandName: isMasterBrand
          ? TARGET_MASTER_BRAND.name
          : wasMasterBrand
            ? ''
            : current.brandName,
        brandType,
        category: isMasterBrand
          ? masterBrandCategory
          : wasMasterBrand
            ? null
            : current.category,
        reason: isMasterBrand
          ? MASTER_BRAND_REASON
          : wasMasterBrand
            ? null
            : current.reason,
        selectedBrand: null,
        slug: isMasterBrand
          ? TARGET_MASTER_BRAND.slug
          : wasMasterBrand
            ? ''
            : current.slug,
      }
    })

  const handleClose = () => {
    setCurrentStep(0)
    setForm(createInitialForm(initialBrandType, initialBrand))
    setSubmitError('')
    onClose()
  }

  const handleSubmit = async () => {
    setSubmitError('')
    try {
      await onSubmit(form)
      handleClose()
    } catch (error) {
      setSubmitError(
        getApiErrorMessage(
          error,
          `We could not ${isEditMode ? 'update' : 'create'} the brand workspace. Please try again.`,
        ),
      )
    }
  }

  const updateSupportingSteward = (index, user) => {
    const supportingStewards = form.supportingStewards.map(
      (steward, itemIndex) => (itemIndex === index ? user : steward),
    )
    updateForm({ supportingStewards })
  }

  const removeSupportingSteward = (index) =>
    updateForm({
      supportingStewards: form.supportingStewards.filter(
        (_steward, itemIndex) => itemIndex !== index,
      ),
    })

  const categoryOptions =
    form.brandType === BRAND_TYPES.MASTER
      ? [masterBrandCategory].filter(Boolean)
      : activeCategories.filter(
          ({ id }) => !masterBrandCategory || id !== masterBrandCategory.id,
        )
  const addSupportingSteward = () =>
    updateForm({
      supportingStewards: [...form.supportingStewards, null],
    })
  const initialEditForm = createInitialForm(initialBrandType, initialBrand)
  const isEditValid =
    isOwnershipValid(form) &&
    Boolean(form.category?.id) &&
    Boolean(form.slug.trim()) &&
    (isMasterBrand
      ? true
      : Boolean(form.brandName.trim()) &&
        isValidBrandSelection(form.selectedBrand))
  const isEditDirty = isEditMode ? hasEditChanges(form, initialEditForm) : true

  const handleBrandSelect = (selectedBrand) =>
    setForm((current) => ({
      ...current,
      brandName: selectedBrand?.brand_name || current.brandName,
      selectedBrand,
      slug: selectedBrand
        ? createBrandSlug(selectedBrand.brand_name)
        : current.slug,
    }))

  if (isEditMode) {
    const editFooter = (
      <div className="edit-brand-modal__footer">
        <p>Changes apply immediately to the brand shell.</p>
        <div className="edit-brand-modal__footer-actions">
          <Button onClick={handleClose} variant="default">
            Cancel
          </Button>
          <Button
            className="edit-brand-modal__save-button"
            isDisabled={!isEditValid || !isEditDirty}
            isLoading={isSubmitting}
            onClick={handleSubmit}
            variant="primary"
          >
            Save changes
          </Button>
        </div>
      </div>
    )

    return (
      <Modal
        className="create-brand-modal edit-brand-modal"
        footer={editFooter}
        headingText={
          <div className="edit-brand-modal__heading">
            <span className="edit-brand-modal__heading-icon" aria-hidden="true">
              <Icon size="lg" svg={PersonIcon} />
            </span>
            <span>
              <span className="create-brand-modal__title">
                Edit brand details
              </span>
              <span className="create-brand-modal__subtitle">
                {form.brandName} · Correct typos or update stewards
              </span>
            </span>
          </div>
        }
        isOpen={isOpen}
        onClose={handleClose}
        scrollBehavior="contained"
        size="lg"
        spacing="none"
      >
        <div
          className="edit-brand-modal__body"
          onMouseDown={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <SubmissionErrorAlert isEditMode message={submitError} />
          <Accordion
            className="edit-brand-modal__accordion"
            header="Brand details"
            preserveDom
            startOpen
          >
            <div className="edit-brand-modal__section-content edit-brand-modal__details">
              {isMasterBrand ? (
                <FormField
                  id="edit-brand-name"
                  isDisabled
                  isRequired
                  label="Brand name"
                  onChange={({ target: { value } }) =>
                    setForm((current) => ({
                      ...current,
                      brandName: value,
                      selectedBrand: null,
                      slug: createBrandSlug(value),
                    }))
                  }
                  type="text"
                  value={form.brandName}
                />
              ) : (
                <BrandLookupField
                  id="edit-brand-name"
                  isDisabled={!canEditBrandIdentity}
                  isRequired
                  label="Brand name"
                  initialQuery={form.brandName}
                  onQueryChange={(brandName) =>
                    handleNameChange({ target: { value: brandName } })
                  }
                  onSelect={handleBrandSelect}
                  search={searchBrands}
                  value={form.selectedBrand}
                />
              )}
              {isMasterBrand && <p>Master brand name cannot be changed.</p>}
              <FormField
                hintText="Used as system identifier."
                id="edit-brand-slug"
                isDisabled={isMasterBrand || !canEditBrandIdentity}
                isRequired={!isMasterBrand}
                label="Brand code / slug"
                onChange={({ target: { value } }) =>
                  updateForm({ slug: value })
                }
                type="text"
                value={form.slug}
              />
              <FormField
                disableFieldInfo={false}
                hintText={
                  isMasterBrand
                    ? 'Category is fixed for the Target Master Brand.'
                    : undefined
                }
                id="edit-brand-category"
                isDisabled={isMasterBrand}
                isRequired={!isMasterBrand}
                label="Portfolio / category"
                onChange={(category) => updateForm({ category })}
                options={categoryOptions}
                placeholder="Select portfolio / category…"
                type="select"
                value={form.category || undefined}
              />
            </div>
          </Accordion>

          <Accordion
            className="edit-brand-modal__accordion"
            header="Brand stewards"
            preserveDom
            startOpen
          >
            <div className="edit-brand-modal__section-content">
              {isMasterBrand && (
                <p>Only primary and supporting stewards can be modified.</p>
              )}
              <fieldset className="edit-brand-modal__primary-steward">
                <legend>
                  Primary steward <span aria-hidden="true">*</span>
                </legend>
                <UserLookupField
                  id="edit-primary-steward"
                  label="Search by email address or ZID"
                  onChange={(primarySteward) => updateForm({ primarySteward })}
                  searchUsers={searchUsers}
                  value={form.primarySteward}
                />
              </fieldset>
              <SupportingStewardsFields
                emptyText="No supporting stewards added yet."
                form={form}
                onAdd={addSupportingSteward}
                onRemove={removeSupportingSteward}
                onUpdate={updateSupportingSteward}
                searchUsers={searchUsers}
              />
            </div>
          </Accordion>
        </div>
      </Modal>
    )
  }

  const canContinue =
    currentStep === 0 ? isDetailsValid(form) : isOwnershipValid(form)
  const footer = (
    <div className="create-brand-modal__footer">
      {currentStep > 0 && (
        <Button
          className="create-brand-modal__back"
          onClick={() => setCurrentStep((step) => step - 1)}
          variant="ghost"
        >
          ← Back
        </Button>
      )}
      <Button onClick={handleClose} variant="default">
        Cancel
      </Button>
      {currentStep < 2 ? (
        <Button
          isDisabled={!canContinue}
          onClick={() => setCurrentStep((step) => step + 1)}
          variant="primary"
        >
          Next
        </Button>
      ) : (
        <Button
          isDisabled={!isDetailsValid(form) || !isOwnershipValid(form)}
          isLoading={isSubmitting}
          onClick={handleSubmit}
          variant="primary"
        >
          Create brand workspace
        </Button>
      )}
    </div>
  )

  return (
    <Modal
      className="create-brand-modal brand-setup-modal--creation"
      footer={footer}
      headingText={<span className="create-brand-modal__title">Add Brand</span>}
      isOpen={isOpen}
      onClose={handleClose}
      scrollBehavior="contained"
      size="md"
      spacing="none"
    >
      <CreationStepper currentStep={currentStep} onSelect={setCurrentStep} />
      <SubmissionErrorAlert isEditMode={false} message={submitError} />

      <div
        className="create-brand-modal__body"
        onPointerDown={(event) => event.stopPropagation()}
      >
        {currentStep === 0 && (
          <div className="create-brand-form">
            <BrandTypeSelector
              onChange={handleBrandTypeChange}
              options={brandTypeOptions}
              value={form.brandType}
            />
            <BrandLookupField
              id="brand-name"
              isDisabled={isMasterBrand}
              isRequired
              label="Brand name"
              initialQuery={form.brandName}
              placeholder="e.g. Wild Fable"
              onQueryChange={(brandName) =>
                handleNameChange({ target: { value: brandName } })
              }
              onSelect={handleBrandSelect}
              search={searchBrands}
              value={form.selectedBrand}
            />
            <FormField
              hintText="Auto-generated from name. Editable — used as a system identifier."
              id="brand-slug"
              isDisabled={isMasterBrand}
              isRequired
              label="Brand code / slug"
              onChange={({ target: { value } }) => updateForm({ slug: value })}
              placeholder="auto-generated from name"
              type="text"
              value={form.slug}
            />
            <FormField
              disableFieldInfo
              id="brand-category"
              isDisabled={isMasterBrand || isLoadingCategories}
              isRequired={form.brandType !== BRAND_TYPES.MASTER}
              key={`brand-category-${form.brandType}`}
              label="Category"
              onChange={(category) => updateForm({ category })}
              options={categoryOptions}
              placeholder={
                isLoadingCategories ? 'Loading categories…' : 'Select category…'
              }
              type="select"
              value={form.category || undefined}
            />
            {categoryError && (
              <p className="create-brand-modal__error" role="alert">
                {categoryError}
              </p>
            )}
            <FormField
              disableFieldInfo
              id="brand-reason"
              isDisabled={isMasterBrand}
              isRequired
              key={`brand-reason-${form.brandType}`}
              label="Reason for adding"
              onChange={(reason) => updateForm({ reason })}
              options={BRAND_REASON_OPTIONS}
              placeholder="Select reason…"
              type="select"
              value={form.reason || undefined}
            />
          </div>
        )}

        {currentStep === 1 && (
          <div className="create-brand-form">
            <UserLookupField
              id="primary-steward"
              isRequired
              label="Primary steward"
              onChange={(primarySteward) => updateForm({ primarySteward })}
              searchUsers={searchUsers}
              value={form.primarySteward}
            />
            <SupportingStewardsFields
              form={form}
              onAdd={addSupportingSteward}
              onRemove={removeSupportingSteward}
              onUpdate={updateSupportingSteward}
              searchUsers={searchUsers}
            />
          </div>
        )}

        {currentStep === 2 && (
          <div className="create-brand-review">
            <p>
              Review the details below and click{' '}
              <strong>Create brand workspace</strong> to create a draft
              workspace. You can add guidance documents after creation.
            </p>
            {/* Read in pairs, two to a row. */}
            <ReviewSection
              rows={[
                { label: 'Brand name', value: form.brandName },
                { label: 'Category', value: form.category?.label },
                { label: 'Brand ID', value: form.selectedBrand?.brand_id },
                {
                  label: 'Brand type',
                  value: BRAND_TYPE_OPTIONS.find(
                    ({ value }) => value === form.brandType,
                  )?.label,
                },
                { label: 'Brand code / slug', value: form.slug },
                {
                  label: 'Reason for adding',
                  value: form.reason?.label,
                },
              ]}
              title="Brand details"
            />
            <OwnershipReview
              primarySteward={form.primarySteward}
              supportingStewards={form.supportingStewards}
            />
          </div>
        )}
      </div>
    </Modal>
  )
}

export default BrandSetupModal
